import React, { useEffect, useRef, useState } from 'react';
import { isRecord, type AdminApiClient, type JsonRecord } from './api-client';

const record = (value: unknown): JsonRecord => (isRecord(value) ? value : {});
const list = (value: unknown): JsonRecord[] => (Array.isArray(value) ? value.filter(isRecord) : []);
function valueText(value: unknown): string {
  if (typeof value !== 'string') return '';
  const text = value.replace(/\r\n?/g, '\n').trim();
  return ['—', '-', '未提供 / 当前权限不可见', '暂未提供'].includes(text) ? '' : text;
}
/** Only the detail page's 产品与行程信息 panel, in its displayed order and masking. */
export function formatOrderSummary(order: JsonRecord): string {
  const trip = record(order.tripInfo);
  const booking = record(trip.bookingDetails),
    summary = record(order.bookingSummary);
  const lines: string[] = ['【产品与行程信息】'];
  const field = (name: string, value: unknown) => {
    const text = valueText(value);
    if (text) lines.push(`${name}：${text}`);
  };
  field(
    '产品 / 套餐',
    `${valueText(order.productName) || '暂未提供'} / ${valueText(order.skuName) || '暂未提供'}`,
  );
  field('出行日期', /^(\d{4}-\d{2}-\d{2})/.exec(valueText(trip.travelDate))?.[1] ?? '待补充');
  const people = trip.peopleCount ?? order.peopleCount;
  field('出行人数', typeof people === 'number' ? String(people) : valueText(people) || '待补充');
  field('联系人', valueText(trip.contactName) || '待补充 / 无字段权限');
  field('联系电话', valueText(trip.mobile) || '待补充 / 无字段权限');
  field('接送点', trip.pickupAddress);
  for (const [key, title] of Object.entries({
    meetingPoint: '集合地点',
    accommodation: '住宿方案',
    lodgingNights: '住宿晚数',
    childBed: '儿童占床',
    vehicle: '出行车辆',
    extras: '附加服务',
    confirmation: '班期确认',
  }))
    field(title, summary[key]);
  if (typeof booking.adults === 'number' || typeof booking.children === 'number') {
    field('成人 / 儿童', `${booking.adults ?? '待补充'} / ${booking.children ?? '待补充'}`);
  }
  const travelers = list(booking.travelers);
  if (travelers.length) {
    lines.push('实际出行人：');
    travelers.forEach((traveler, index) => {
      lines.push(
        [
          `${index + 1}. ${valueText(traveler.name) || '姓名未采集'}`,
          valueText(traveler.documentNumber)
            ? `${traveler.documentType === 'PASSPORT' ? '护照' : '身份证'}：${valueText(traveler.documentNumber)}`
            : '',
          valueText(traveler.mobile),
        ]
          .filter(Boolean)
          .join('  '),
      );
    });
  }
  return lines.join('\n');
}

export function CopyOrderButton({
  api,
  appId,
  orderId,
}: {
  api: AdminApiClient;
  appId: string;
  orderId: string;
}) {
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [manualText, setManualText] = useState('');
  const controller = useRef<AbortController>();
  const opener = useRef<HTMLButtonElement>(null),
    textarea = useRef<HTMLTextAreaElement>(null);
  useEffect(() => () => controller.current?.abort(), [api, appId, orderId]);
  useEffect(() => {
    if (manualText) {
      textarea.current?.focus();
      textarea.current?.select();
    }
  }, [manualText]);
  function closeManual() {
    setManualText('');
    opener.current?.focus();
  }
  async function copy() {
    if (controller.current) return;
    const active = new AbortController();
    controller.current = active;
    setBusy(true);
    setFeedback('');
    let timeout: ReturnType<typeof setTimeout> | undefined;
    try {
      const data = await Promise.race([
        api.request<JsonRecord>(
          `/api/admin/m4/orders/${encodeURIComponent(orderId)}?appId=${encodeURIComponent(appId)}`,
          { signal: active.signal, cache: 'no-store' },
        ),
        new Promise<never>((_, reject) => {
          timeout = setTimeout(() => reject(new Error('读取超时，请稍后重试复制')), 8000);
        }),
      ]);
      clearTimeout(timeout);
      if (active.signal.aborted) return;
      if (!isRecord(data) || data.id !== orderId || !data.orderNo)
        throw new Error('订单详情不完整，请刷新后重试');
      const text = formatOrderSummary(data);
      try {
        if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
        await navigator.clipboard.writeText(text);
        if (!active.signal.aborted) setFeedback('已复制');
      } catch {
        if (!active.signal.aborted) {
          setManualText(text);
          setFeedback('请手动复制');
        }
      }
    } catch (error) {
      if (!active.signal.aborted)
        setFeedback(error instanceof Error ? error.message : '读取失败，请重试');
    } finally {
      clearTimeout(timeout);
      active.abort();
      if (controller.current === active) {
        controller.current = undefined;
        setBusy(false);
      }
    }
  }
  return (
    <>
      <button
        type="button"
        ref={opener}
        className="order-copy-button"
        disabled={busy}
        onClick={() => void copy()}
      >
        {busy ? '读取中…' : '复制订单'}
      </button>
      {feedback && (
        <small role="status" className="order-copy-feedback">
          {feedback}
        </small>
      )}
      {manualText && (
        <div className="order-dialog-backdrop">
          <section
            role="dialog"
            aria-modal="true"
            aria-label="手动复制订单摘要"
            className="order-dialog order-copy-dialog"
            onKeyDown={(event) => {
              if (event.key === 'Escape') closeManual();
              if (event.key === 'Tab') {
                const controls = Array.from(
                  event.currentTarget.querySelectorAll<HTMLElement>('textarea,button'),
                );
                if (event.shiftKey && document.activeElement === controls[0]) {
                  event.preventDefault();
                  controls.at(-1)?.focus();
                } else if (!event.shiftKey && document.activeElement === controls.at(-1)) {
                  event.preventDefault();
                  controls[0]?.focus();
                }
              }
            }}
          >
            <h2>复制订单摘要</h2>
            <p>浏览器未允许自动写入剪贴板。文本已选中，请按 Ctrl+C（Mac 使用 ⌘C）复制。</p>
            <textarea
              ref={textarea}
              aria-label="订单摘要文本"
              readOnly
              value={manualText}
              onFocus={(event) => event.currentTarget.select()}
            />
            <footer>
              <button type="button" onClick={() => textarea.current?.select()}>
                全选文本
              </button>
              <button type="button" onClick={closeManual}>
                关闭
              </button>
            </footer>
          </section>
        </div>
      )}
    </>
  );
}
